/**
 * Slash commands (the Trilium/AFFiNE pattern): type "/" and pick a block —
 * no markdown memorization needed. Runs through CodeMirror's autocomplete
 * so it is keyboard-first, fuzzy-filtered, and styled like the rest.
 */
import type { Completion, CompletionContext, CompletionResult } from "@codemirror/autocomplete";
import { EditorSelection } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";
import { handleFiles } from "./attachments";

interface SlashItem {
  label: string;
  detail: string;
  section: string;
  keywords?: string;
  /** snippet with $ marking the final cursor position (one $ max) */
  snippet?: string;
  /** custom action instead of a snippet */
  action?: (view: EditorView, from: number) => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function nowTime(): string {
  return new Date().toTimeString().slice(0, 5);
}

const ITEMS: SlashItem[] = [
  { label: "Heading 1", detail: "#", section: "Blocks", snippet: "# $" },
  { label: "Heading 2", detail: "##", section: "Blocks", snippet: "## $" },
  { label: "Heading 3", detail: "###", section: "Blocks", snippet: "### $" },
  { label: "Subtext", detail: "-#", section: "Blocks", keywords: "small muted caption footnote", snippet: "-# $" },
  { label: "Bullet list", detail: "-", section: "Blocks", snippet: "- $" },
  { label: "Numbered list", detail: "1.", section: "Blocks", snippet: "1. $" },
  { label: "Task list", detail: "- [ ]", section: "Blocks", keywords: "todo checkbox", snippet: "- [ ] $" },
  { label: "Quote", detail: ">", section: "Blocks", snippet: "> $" },
  { label: "Divider", detail: "---", section: "Blocks", keywords: "hr rule separator", snippet: "---\n$" },
  {
    label: "Code block",
    detail: "```",
    section: "Blocks",
    keywords: "fence syntax",
    snippet: "```\n$\n```",
  },
  {
    label: "Table",
    detail: "3×3",
    section: "Blocks",
    keywords: "grid sheet spreadsheet",
    snippet: "| Column 1 | Column 2 | Column 3 |\n| --- | --- | --- |\n| $ |  |  |\n|  |  |  |\n",
  },
  {
    label: "Callout",
    detail: "note",
    section: "Blocks",
    keywords: "admonition info tip warning",
    snippet: "> [!note] $\n> ",
  },
  { label: "Wikilink", detail: "[[…]]", section: "Insert", keywords: "internal note link", snippet: "[[$]]" },
  { label: "Link", detail: "[…](…)", section: "Insert", keywords: "url web", snippet: "[$](url)" },
  { label: "Tag", detail: "#tag", section: "Insert", snippet: "#$" },
  { label: "Today's date", detail: "", section: "Insert", keywords: "date day", get snippet() { return `${today()}$`; } },
  { label: "Current time", detail: "", section: "Insert", keywords: "clock now", get snippet() { return `${nowTime()}$`; } },
  {
    label: "Date and time",
    detail: "",
    section: "Insert",
    keywords: "timestamp now datetime",
    get snippet() {
      return `${today()} ${nowTime()}$`;
    },
  },
  {
    label: "Attach file or image",
    detail: "upload",
    section: "Insert",
    keywords: "picture photo pdf upload",
    action: (view, from) => {
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.onchange = () => {
        if (input.files?.length) handleFiles(view, input.files, from);
      };
      input.click();
    },
  },
];

function applyItem(item: SlashItem) {
  // `from` is just AFTER the "/" (the filterable range); the slash itself
  // must be removed too, hence from - 1 everywhere.
  return (view: EditorView, _completion: Completion, from: number, to: number) => {
    const start = from - 1;
    if (item.action) {
      view.dispatch({ changes: { from: start, to, insert: "" } });
      item.action(view, start);
      return;
    }
    const snippet = item.snippet ?? "";
    const cursorIdx = snippet.indexOf("$");
    const insert = snippet.replace("$", "");
    const cursor = start + (cursorIdx === -1 ? insert.length : cursorIdx);
    view.dispatch({
      changes: { from: start, to, insert },
      selection: EditorSelection.cursor(cursor),
      scrollIntoView: true,
      userEvent: "input",
    });
  };
}

export function slashSource(context: CompletionContext): CompletionResult | null {
  // "/" at line start or after whitespace; never mid-word
  const match = context.matchBefore(/(?:^|\s)\/[\w-]*$/);
  if (!match) return null;
  const slashAt = context.state.sliceDoc(match.from, match.from + 1) === "/" ? match.from : match.from + 1;

  return {
    from: slashAt + 1, // filter on the text AFTER the slash
    options: ITEMS.map((item) => ({
      label: item.label,
      detail: item.detail,
      section: item.section,
      boost: item.section === "Blocks" ? 1 : 0,
      apply: applyItem(item),
    })),
    validFor: /^[\w-]*$/,
  };
}
